import { isNativePlatform } from '@/lib/platform/platformDetection'

/**
 * Get the API base URL based on platform and environment.
 *
 * Web: Uses relative URLs (same origin via Docker/Caddy)
 * Native (iOS/Android): Requires VITE_API_URL to be set (Capacitor WebView can't use relative URLs)
 *
 * For local native development, set VITE_API_URL to your Mac's local IP:
 * VITE_API_URL=https://192.168.1.100
 */
export function getApiBaseUrl(): string {
  const configuredUrl = import.meta.env.VITE_API_URL

  if (isNativePlatform()) {
    if (configuredUrl) {
      return configuredUrl
    }

    // In development, throw to fail fast and make the error obvious
    if (import.meta.env.DEV) {
      throw new Error(
        '[API Config] Native platform requires VITE_API_URL environment variable. ' +
          'Set it in .env.local: VITE_API_URL=https://YOUR_IP_OR_SERVER',
      )
    }

    console.error(
      '[API Config] Native platform requires VITE_API_URL to be set. ' + 'API requests will fail.',
    )
    return ''
  }

  return configuredUrl || ''
}

/**
 * Get the full URL for a given path.
 * Handles both relative and absolute URLs.
 */
export function getFullApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl()

  if (!baseUrl) {
    return path
  }

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }

  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const cleanPath = path.startsWith('/') ? path : `/${path}`

  return `${cleanBase}${cleanPath}`
}

/**
 * Check if we're running on a native platform (iOS or Android)
 * Re-exported from centralized platform detection for convenience
 */
export { isNativePlatform } from '@/lib/platform/platformDetection'
